import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import instance from "../api/api";
import { queryClient } from "../../App";
import { toast } from "react-toastify";
import instancePos from "../api/apiV2";
import axios from "axios";

// Shift a "YYYY-MM-DD" string by n days (UTC math, no timezone drift).
const shiftDate = (date, n) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Module-level so useQueries keeps the merged object stable between renders.
const mergeReportStatus = (results) => ({
  isLoading: results.some((r) => r.isLoading),
  data: results.every((r) => r.data)
    ? {
        data: Object.assign({}, ...results.map((r) => r.data.data)),
        // each day's map only covers that day's restaurants
        id_name_map: Object.assign({}, ...results.map((r) => r.data.id_name_map)),
      }
    : undefined,
});

// One request per day, fetched in parallel; each day is cached on its own,
// so stepping the date picker by a day only fetches the new day.
export const useGetReportStatus = (report_date, onlySelectedDay = false) => {
  const dates = onlySelectedDay
    ? [report_date]
    : [-2, -1, 0, 1].map((n) => shiftDate(report_date, n));
  return useQueries({
    queries: dates.map((date) => ({
      queryKey: ["master-report-status", date],
      queryFn: async () => {
        const response = await instance.post(`/get-report-status`, {
          report_date: date,
          single_date: true,
        });
        // keep only the requested day, even if an older backend returns its 4-day window
        return {
          ...response.data,
          data: { [date]: response.data.data?.[date] ?? {} },
        };
      },
      meta: {
        errorMsg: true,
      },
    })),
    combine: mergeReportStatus,
  });
};

export const getTaskScreenshots = async (task_id) => {
  const response = await instance.post(`/get-task-screenshots`, { task_id });
  return response.data.data;
};

export const useStopTask = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await instancePos.post(`/stop_task`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["all-schedulers"] });
    },
    onError: (error) => {
      toast.error(error.response.data.message || "Uh Oh! Something went wrong");
    },
  });
};

export const useStartTask = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await instancePos.post(`/start_task`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["all-schedulers"] });
      queryClient.invalidateQueries({ queryKey: ["master-report-status"] });
    },
    onError: (error) => {
      toast.error(error.response.data.message || "Uh Oh! Something went wrong");
    },
  });
};

export const useStopScheduler = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await instancePos.post(`/stop_scheduler`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all-schedulers"] });
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.response.data.message || "Uh Oh! Something went wrong");
    },
  });
};

export const useGetCalendricView = (startDate, endDate) => {
  return useQuery({
    queryKey: ["calendric-view", startDate, endDate],
    queryFn: async () => {
      if (!startDate || !endDate) {
        return [];
      }
      try {
        // Use GET method since POST is not allowed
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/calendric-view`, {
          params: {
            start_date: startDate,
            end_date: endDate
          },
          headers: {
            // 'Authorization': `Bearer ${localStorage.getItem('token')}` // Commented out for testing
          }

        });
        console.log('Calendric view API response:', response.data);
        return response.data || [];
      } catch (error) {
        console.error('Calendric view API error:', error.message);
        if (error.response) {
          console.error('Error status:', error.response.status);
          console.error('Error data:', error.response.data);
        } else if (error.request) {
          console.error('Network error - no response received');
        }
        throw error;
      }
    },
    enabled: !!startDate && !!endDate,
    meta: {
      errorMsg: true,
    },
    retry: 1,
  });
};

export const useUpdateJobStatus = () => {
  return useMutation({
    mutationFn: async ({ task_id, job_status, report_date, restaurant_id }) => {
      const response = await instance.post(`/api/update-scraper-status`, { task_id, job_status, report_date, restaurant_id });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.response.data.message || "Uh Oh! Something went wrong");
    },
  });
};

export const useGetPosStatus = () => {
  return useQuery({
    queryKey: ["pos-status"],
    queryFn: async () => {
      const response = await instance.get(`/pos`);
      return response.data.data;
    },
    meta: {
      errorMsg: true,
    },
  });
};

export const useUpdatePosStatus = () => {
  return useMutation({
    mutationFn: async ({ pos_id, pos_working }) => {
      const response = await instance.patch(`/pos/${pos_id}/status`, { pos_working });
      return response.data.data;
    },
    // swap the row in the cache so it moves groups without a refetch
    onSuccess: (pos) => {
      queryClient.setQueryData(["pos-status"], (list) =>
        list?.map((p) => (p.pos_id === pos.pos_id ? pos : p))
      );
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Uh Oh! Something went wrong");
    },
  });
};

// Simplified per-restaurant sync status for one day (formerly the crunch-analytics iframe).
export const useGetSimplifiedReportStatus = (report_date) =>
  useQuery({
    queryKey: ["simplified-report-status", report_date],
    queryFn: () =>
      instance
        .post(`/get-simplified-report-status`, { report_date })
        .then((res) => res.data?.data ?? [])
        // backend answers 400 when the day has no rows: show an empty table
        .catch((err) => {
          if (err.response?.status === 400) return [];
          throw err;
        }),
  });
